import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Eye, Trash2, X, HandCoins, ShoppingCart, ClipboardList, CheckCircle2, Sparkles, Calendar, Clock, MapPin, Navigation, Zap, Crosshair, Smartphone, Building2, CreditCard, Banknote, Ticket } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useOrders } from './OrdersContext';
import { useBranches, isKdsEnabled } from './BranchesContext';
import { useSettings } from './SettingsContext';
import { PaymentMethod, paymentMethodLabel } from './OrdersContext';
import { useQuestions } from './QuestionsContext';
import { useBranchMenu } from './MenusContext';
import { useOutlets } from './OutletsContext';
import { useCart } from './CartContext';
import { useCustomerAuth } from './CustomerAuthContext';
import { usePromotions, Promotion } from './PromotionsContext';
import { useCRM } from './CRMContext';
import { useQrSession } from './QrSessionContext';
import { useScreenInit } from '../useScreenInit.js';
type TipMode = 'none' | 'preset' | 'custom';
type CartItem = {
  id: string;
  name: string;
  qty: number;
  unitPrice: number;
};
function formatETB(n: number) {
  return `ETB ${Math.round(n).toLocaleString()}`;
}
export function CheckoutPreview() {
  const navigate = useNavigate();
  const {
    addOrder
  } = useOrders();
  const {
    branches,
    branchId,
    setBranchId
  } = useBranches();
  const {
    general
  } = useSettings();
  const branchMenu = useBranchMenu(branchId);
  const {
    outletId,
    getOutletMenu
  } = useOutlets();
  const {
    lines,
    removeLine,
    clear
  } = useCart();
  const {
    customer,
    awardPoints
  } = useCustomerAuth();
  const branch = branches.find(b => b.id === branchId) || branches[0];
  const {
    qrContext,
    tableNumber
  } = useQrSession();
  const init = useScreenInit() || {};
  // Allow opening checkout standalone against a specific branch via ?branch=<id>
  // (e.g. previewing the takeout-only dine-in block in a Screens iframe).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const b = new URLSearchParams(window.location.search).get('branch');
    if (b && branches.some(x => x.id === b) && b !== branchId) {
      setBranchId(b);
    }
  }, [branches, branchId, setBranchId]);
  // Each admin order method maps to its own customer-facing option, labeled
  // correctly: Takeout, Order Ahead (scheduled pickup), and Delivery.
  const canTakeout = !!branch?.methods?.takeout;
  const canOrderAhead = !!(branch?.methods?.orderAhead || branch?.methods?.dineAhead);
  const canDeliver = !!branch?.methods?.delivery;
  const canDineIn = !!branch?.methods?.dineIn;
  const isTableContext = qrContext === 'table';
  // Off-premise channels that don't require being at a table.
  const hasOffPremise = canTakeout || canOrderAhead || canDeliver;
  // When a guest scans a table QR at a branch that doesn't take tableside
  // orders but DOES offer takeout/delivery, we let them switch into the normal
  // off-premise checkout instead of hitting a dead end.
  const [switchedToOffPremise, setSwitchedToOffPremise] = useState(false);
  const effectiveTable = isTableContext && canDineIn && !switchedToOffPremise;
  // The table QR landed somewhere that can't take tableside orders, but the
  // restaurant still accepts takeout/delivery — offer those as a fallback.
  const canOfferOffPremiseFallback = isTableContext && !canDineIn && hasOffPremise && !switchedToOffPremise;
  const hasOnlineMethods = effectiveTable ? canDineIn : hasOffPremise;
  const firstAvailableMethod: 'takeout' | 'orderAhead' | 'delivery' = canTakeout ? 'takeout' : canOrderAhead ? 'orderAhead' : 'delivery';
  const [tipMode, setTipMode] = useState<TipMode>('none');
  const [tipPresetIdx, setTipPresetIdx] = useState(1); // default to middle preset
  const [customTipInput, setCustomTipInput] = useState('');
  const [sheetOpen, setSheetOpen] = useState(!!init.sheetOpen);
  const [detailsNotified, setDetailsNotified] = useState(false);
  const {
    captureLead
  } = useCRM();
  const handleDetailsNotify = () => {
    if (customer?.phone) {
      captureLead(customer.phone, {
        marketingOptIn: true,
        notes: 'Requested notification for online ordering availability.'
      });
    }
    setDetailsNotified(true);
  };
  const [orderMethod, setOrderMethod] = useState<'takeout' | 'orderAhead' | 'delivery' | 'table'>(effectiveTable ? 'table' : firstAvailableMethod);
  // Keep the selected order method in sync with what the branch currently
  // enables. Toggling order methods on the admin side updates the shared
  // branch, and this ensures the customer checkout reflects it live: if the
  // chosen method gets disabled, fall back to one that is still available.
  useEffect(() => {
    if (effectiveTable) {
      if (orderMethod !== 'table') setOrderMethod('table');
      return;
    }
    const stillAvailable = orderMethod === 'takeout' && canTakeout || orderMethod === 'orderAhead' && canOrderAhead || orderMethod === 'delivery' && canDeliver;
    if (!stillAvailable && hasOffPremise) {
      setOrderMethod(firstAvailableMethod);
    }
  }, [effectiveTable, canTakeout, canOrderAhead, canDeliver, hasOffPremise, firstAvailableMethod, orderMethod]);
  // Pickup scheduling (Order Ahead)
  const [pickupMode, setPickupMode] = useState<'asap' | 'scheduled'>('asap');
  const [pickupDateIdx, setPickupDateIdx] = useState(0);
  const [pickupTime, setPickupTime] = useState<string | null>(null);
  // Delivery
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryGPS, setDeliveryGPS] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [bankTransferRef, setBankTransferRef] = useState('');
  const [selectedBankAccountId, setSelectedBankAccountId] = useState<string | undefined>(branch?.bankAccounts?.find(a => a.active)?.id);
  const orderRef = useMemo(() => `QR-${Math.floor(Math.random() * 10000)}`, []);
  const cartItems = useMemo(() => lines.map(l => ({
    id: l.lineId,
    itemId: l.itemId,
    name: l.name,
    qty: l.qty,
    unitPrice: l.unitPrice,
    modifiers: l.modifiers,
    notes: l.notes
  })), [lines]);
  const items = cartItems;
  const {
    questions
  } = useQuestions();
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  // Derived totals
  const {
    validate,
    computeDiscount
  } = usePromotions();
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState<Promotion | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);
  const handleApplyPromo = () => {
    setPromoError(null);
    const {
      ok,
      promo,
      reason
    } = validate(promoCodeInput, subtotal);
    if (ok && promo) {
      setAppliedPromo(promo);
      setPromoCodeInput('');
    } else {
      setPromoError(reason || 'Invalid promo code');
    }
  };
  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoError(null);
  };
  const subtotal = items.reduce((sum, item) => sum + item.unitPrice * item.qty, 0);
  const discountAmount = appliedPromo ? computeDiscount(appliedPromo, subtotal) : 0;
  const discountedSubtotal = Math.max(0, subtotal - discountAmount);
  const tax = discountedSubtotal * (branch?.taxRatePct || 0) / 100;
  const service = discountedSubtotal * (branch?.serviceFeePct || 0) / 100;
  // Tipping is configured per-branch only. When a branch hasn't enabled
  // tipping, the checkout hides the tip UI entirely.
  const effectiveTipping = branch?.tippingOverride ?? {
    customerTippingEnabled: false,
    suggestedTipPcts: [0, 0, 0]
  };
  const tipPct = effectiveTipping.suggestedTipPcts[tipPresetIdx] || 0;
  const customTip = parseFloat(customTipInput);
  const tipAmount = tipPct !== null ? discountedSubtotal * tipPct / 100 : customTip;
  const total = discountedSubtotal + tax + service + tipAmount;
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('telebirr');
  // Payment-method commission is passed on to the customer: it is silently
  // folded into the final amount they pay at the payment step. It is NOT
  // itemized anywhere in the checkout flow — the cart/details screens show the
  // base total, and only the final "Place Order" sheet reflects the higher
  // amount (including the fee for the chosen method).
  const commissionRate = general.commissionRates?.[paymentMethod] || 0;
  const commissionFee = +(total * commissionRate / 100).toFixed(2);
  const finalTotal = +(total + commissionFee).toFixed(2);
  const [isPaymentMethodSelected, setIsPaymentMethodSelected] = useState(false);
  const [step, setStep] = useState<'cart' | 'details'>(init.step ?? 'cart');
  const applicableQuestions = useMemo(() => {
    const cartItemIds = new Set(cartItems.map(i => i.id));
    return questions.filter(q => {
      if (!q.active) return false;
      if (q.scope === 'all_orders') return true;
      if (q.scope === 'item_triggered' && q.itemIds) {
        return q.itemIds.some(id => cartItemIds.has(id));
      }
      return false;
    });
  }, [questions, cartItems]);
  const missingRequiredQuestions = useMemo(() => {
    return applicableQuestions.some(q => {
      if (!q.required) return false;
      const ans = answers[q.id];
      if (q.type === 'multi-select') {
        return !ans || Array.isArray(ans) && ans.length === 0;
      }
      return !ans || String(ans).trim() === '';
    });
  }, [applicableQuestions, answers]);
  const handlePlaceOrder = () => {
    setIsSubmitting(true);
    const activeBranch = branches.find(b => b.id === branchId) || branches[0];
    const kdsEnabled = isKdsEnabled(activeBranch);
    const now = Date.now();
    // Commission is funded by the customer (already folded into finalTotal),
    // but the restaurant still records & remits it: online rails auto-settle,
    // bank transfers accrue as owed until the cashier verifies the transfer.
    const commissionAmount = commissionFee;
    const commissionStatus = paymentMethod === 'bank-transfer' ? undefined : 'settled';
    setTimeout(() => {
      addOrder({
        id: orderRef,
        customerName: customer?.name || 'Guest',
        customerPhone: customer?.phone || '',
        branchId: activeBranch?.id,
        outletId: outletId || undefined,
        method: effectiveTable ? 'table' : orderMethod === 'delivery' ? 'delivery' : orderMethod === 'orderAhead' ? 'dineAhead' : 'takeout',
        tableNumber: effectiveTable && tableNumber ? tableNumber : undefined,
        // Always start at 'new' so the cashier has to explicitly accept.
        // In counter mode the cashier's "Accept" jumps straight to 'ready'.
        status: 'new',
        paymentStatus: paymentMethod === 'cash' || paymentMethod === 'bank-transfer' ? 'pending' : 'paid',
        paymentMethod,
        bankTransferRef: paymentMethod === 'bank-transfer' ? bankTransferRef : undefined,
        selectedBankAccountId: paymentMethod === 'bank-transfer' ? selectedBankAccountId : undefined,
        commissionRate,
        commissionAmount,
        commissionStatus,
        items: cartItems.map(item => ({
          id: item.itemId,
          name: item.name,
          quantity: item.qty,
          unitPrice: item.unitPrice,
          modifiers: item.modifiers,
          notes: item.notes
        })),
        subtotal,
        packagingFee: 0,
        serviceFee: service,
        discountCode: appliedPromo?.code,
        discountAmount: discountAmount > 0 ? discountAmount : undefined,
        // Amount the customer actually pays — base total + the payment-method
        // commission, which is passed through to the customer (not itemized).
        total: finalTotal,
        placedAt: now,
        estimatedMinutes: 15,
        answers: Object.entries(answers).map(([qId, ans]) => {
          const q = questions.find(x => x.id === qId);
          return {
            questionId: qId,
            question: q?.question || '',
            answer: ans
          };
        })
      });
      setIsSubmitting(false);
      setOrderPlaced(true);
      if (awardPoints) {
        awardPoints(total);
      }
      clear();
    }, 1500);
  };
  return <div className="min-h-screen w-full bg-gray-100 flex items-stretch justify-center font-sans" data-id="element-1005">
      {/* Phone frame on desktop, full bleed on mobile.
                                                                              Explicit height is required because the screen motion.divs
                                                                              are positioned `absolute inset-0` for slide transitions. */}
      <div className="w-full md:max-w-md bg-white shadow-xl md:my-6 md:rounded-3xl overflow-hidden relative flex flex-col h-[100dvh] md:h-[calc(100vh-3rem)]" data-id="element-1006">
        <AnimatePresence mode="wait" initial={false} data-id="element-1007">
          {step === 'cart' && <motion.div key="cart" initial={{
          x: -20,
          opacity: 0
        }} animate={{
          x: 0,
          opacity: 1
        }} exit={{
          x: -20,
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="absolute inset-0 flex flex-col bg-gray-100" data-id="element-1008">
              {/* Top bar */}
              <header className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white sticky top-0 z-10" data-id="element-1009">
                <button className="p-1 -ml-1 text-gray-700 hover:text-gray-900 flex items-center gap-1" data-id="element-1010">
                  <ChevronLeft size={20} data-id="element-1011" />
                  <span className="font-bold text-base" data-id="element-1012">Your Cart</span>
                </button>
                <span className="text-xs text-gray-500" data-id="element-1013">
                  {cartItems.length} item{cartItems.length === 1 ? '' : 's'}
                </span>
              </header>

              {/* Scrollable content */}
              <main className="flex-1 overflow-y-auto px-4 py-4 space-y-3 pb-32" data-id="element-1014">
                {/* Items */}
                {cartItems.length === 0 && <div className="bg-white border border-gray-100 rounded-xl p-8 text-center" data-id="element-1015">
                    <div className="w-12 h-12 rounded-2xl bg-gray-100 text-gray-400 flex items-center justify-center mx-auto mb-3" data-id="element-1016">
                      <ShoppingCart size={22} data-id="element-1017" />
                    </div>
                    <p className="text-sm font-semibold text-gray-900" data-id="element-1018">
                      Your cart is empty
                    </p>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-1019">
                      Add a few dishes from the menu to get started.
                    </p>
                    <button onClick={() => navigate('/menu')} className="mt-4 inline-flex items-center justify-center px-4 py-2.5 rounded-xl bg-[#0f172a] text-white text-sm font-bold hover:bg-[#1e293b] transition-colors" data-id="element-1020">
                      Browse the menu
                    </button>
                  </div>}
                <div className={`bg-white border border-gray-100 rounded-xl divide-y divide-gray-100 ${cartItems.length === 0 ? 'hidden' : ''}`} data-id="element-1021">
                  {cartItems.map(item => <div key={item.id} className="flex items-center justify-between gap-2 p-3" data-id="element-1022">
                      <div className="min-w-0 flex-1" data-id="element-1023">
                        <div className="font-bold text-gray-900 text-sm truncate" data-id="element-1024">
                          {item.name}
                        </div>
                        {item.modifiers && item.modifiers.length > 0 && <div className="text-[11px] text-gray-500 mt-0.5 leading-snug" data-id="element-1025">
                            {item.modifiers.map(m => `+ ${m.name}`).join(', ')}
                          </div>}
                        {item.notes && <div className="text-[11px] italic text-gray-400 mt-0.5 leading-snug" data-id="element-1026">
                            "{item.notes}"
                          </div>}
                        <div className="text-xs text-gray-500 mt-1" data-id="element-1027">
                          Qty: {item.qty}
                        </div>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-1028">
                        <span className="font-bold text-gray-900 text-sm tabular-nums" data-id="element-1029">
                          {formatETB(item.unitPrice * item.qty)}
                        </span>
                        <button className="text-gray-400 hover:text-gray-600 p-1" aria-label="View item" data-id="element-1030">
                          <Eye size={15} data-id="element-1031" />
                        </button>
                        <button onClick={() => removeLine(item.id)} className="text-red-500 hover:text-red-600 p-1" aria-label="Remove item" data-id="element-1032">
                          <Trash2 size={15} data-id="element-1033" />
                        </button>
                      </div>
                    </div>)}
                </div>

                {/* Order Summary */}
                <div className="bg-white border border-gray-100 rounded-xl p-4" data-id="element-1034">
                  <h2 className="font-bold text-gray-900 text-base mb-3" data-id="element-1035">
                    Order Summary
                  </h2>
                  <SummaryRow label="Subtotal" value={subtotal} data-id="element-1036" />
                  <SummaryRow label={`Tax (${branch?.taxRatePct || 0}%)`} value={tax} data-id="element-1037" />
                  {service > 0 && <SummaryRow label={`Service (${branch?.serviceFeePct || 0}%)`} value={service} data-id="element-1038" />}
                  <div className="border-t border-gray-200 mt-3 pt-3 flex items-center justify-between" data-id="element-1039">
                    <span className="font-bold text-gray-900 text-base" data-id="element-1040">
                      Total
                    </span>
                    <motion.span key={subtotal + tax + service} initial={{
                  scale: 0.95
                }} animate={{
                  scale: 1
                }} className="font-bold text-gray-900 text-base tabular-nums" data-id="element-1041">
                      {formatETB(subtotal + tax + service)}
                    </motion.span>
                  </div>
                </div>
              </main>

              {/* Bottom action bar */}
              <div className="absolute left-0 right-0 bottom-0 px-4 py-3 bg-white border-t border-gray-200 space-y-2" data-id="element-1042">
                <button onClick={() => setStep('details')} disabled={cartItems.length === 0} className="w-full py-3.5 rounded-xl bg-[#0f172a] text-white font-bold text-sm hover:bg-[#1e293b] transition-colors disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed" data-id="element-1043">
                  Continue to details · {formatETB(subtotal + tax + service)}
                </button>
                <button onClick={() => navigate('/menu')} className="w-full py-3 rounded-xl border border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors" data-id="element-1044">
                  Add More Items
                </button>
              </div>
            </motion.div>}

          {step === 'details' && <motion.div key="details" initial={{
          x: 20,
          opacity: 0
        }} animate={{
          x: 0,
          opacity: 1
        }} exit={{
          x: 20,
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="absolute inset-0 flex flex-col bg-gray-100" data-id="element-1045">
              <header className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white sticky top-0 z-10" data-id="element-1046">
                <button onClick={() => setStep('cart')} className="p-1 -ml-1 text-gray-700 hover:text-gray-900 flex items-center gap-1" data-id="element-1047">
                  <ChevronLeft size={20} data-id="element-1048" />
                  <span className="font-bold text-base" data-id="element-1049">Details</span>
                </button>
                <span className="text-xs text-gray-500" data-id="element-1050">
                  {cartItems.length} item{cartItems.length === 1 ? '' : 's'}
                </span>
              </header>

              <main className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4 space-y-4 pb-40" data-id="element-1051">
                {canOfferOffPremiseFallback && <div className="bg-white border border-gray-100 rounded-2xl p-6 text-center mt-2" data-id="element-1052">
                    <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1053">
                      <MapPin size={28} data-id="element-1054" />
                    </div>
                    <h2 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1055">
                      Tableside ordering isn't available here
                    </h2>
                    <p className="text-sm text-gray-500 mb-1 max-w-xs mx-auto" data-id="element-1056">
                      This restaurant doesn't take orders from the table —
                      please wait for a waiter to come take your order.
                    </p>
                    <p className="text-sm text-gray-700 font-medium mb-6 max-w-xs mx-auto" data-id="element-1057">
                      Good news — you can still order for{' '}
                      {(() => {
                  const opts = [canTakeout && 'takeout', canOrderAhead && 'pickup ahead', canDeliver && 'delivery'].filter(Boolean) as string[];
                  if (opts.length <= 1) return opts[0] || '';
                  if (opts.length === 2) return `${opts[0]} or ${opts[1]}`;
                  return `${opts.slice(0, -1).join(', ')}, or ${opts[opts.length - 1]}`;
                })()}
                      .
                    </p>
                    <button onClick={() => setSwitchedToOffPremise(true)} className="w-full max-w-xs mx-auto py-3 rounded-xl bg-[#0f172a] text-white font-bold text-sm hover:bg-[#1e293b] transition-colors" data-id="element-1058">
                      Continue with{' '}
                      {canTakeout ? 'takeout' : canDeliver ? 'delivery' : 'pickup'}
                    </button>
                  </div>}
                {!canOfferOffPremiseFallback && !hasOnlineMethods && <div className="bg-white border border-gray-100 rounded-2xl p-6 text-center mt-2" data-id="element-1059">
                    <div className="w-16 h-16 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1060">
                      <MapPin size={28} data-id="element-1061" />
                    </div>
                    <h2 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1062">
                      {isTableContext ? "Ordering from your table isn't available here" : "Online ordering isn't available at this location yet"}
                    </h2>
                    <p className="text-sm text-gray-500 mb-6 max-w-xs mx-auto" data-id="element-1063">
                      Please order at the counter. We saved your profile for
                      next time.
                    </p>
                    {customer?.name && <div className="bg-gray-50 rounded-xl p-3 mb-5 border border-gray-100 flex items-center gap-3 text-left max-w-xs mx-auto" data-id="element-1064">
                        <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center font-bold text-gray-600" data-id="element-1065">
                          {customer.name.charAt(0)}
                        </div>
                        <div data-id="element-1066">
                          <div className="font-bold text-gray-900 text-sm" data-id="element-1067">
                            {customer.name}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-1068">
                            {customer.phone}
                          </div>
                        </div>
                      </div>}
                    {detailsNotified ? <div className="bg-emerald-50 text-emerald-700 p-3 rounded-xl font-bold flex items-center justify-center gap-2 max-w-xs mx-auto" data-id="element-1069">
                        <CheckCircle2 size={18} data-id="element-1070" />
                        You're on the list!
                      </div> : <button onClick={handleDetailsNotify} className="w-full max-w-xs mx-auto py-3 rounded-xl bg-[#0f172a] text-white font-bold text-sm hover:bg-[#1e293b] transition-colors" data-id="element-1071">
                        Notify me when ordering launches here
                      </button>}
                  </div>}
                {hasOnlineMethods && !canOfferOffPremiseFallback && <>
                    {/* Order Method */}
                    <div className="bg-white border border-gray-100 rounded-xl p-4" data-id="element-1072">
                      <h3 className="font-bold text-gray-900 text-sm mb-3" data-id="element-1073">
                        Order Method
                      </h3>

                      {effectiveTable ? <div className="p-4 rounded-xl bg-purple-50 border border-purple-100 flex items-center gap-3" data-id="element-1074">
                          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600" data-id="element-1075">
                            <MapPin size={20} data-id="element-1076" />
                          </div>
                          <div data-id="element-1077">
                            <div className="font-bold text-purple-900 text-sm" data-id="element-1078">
                              Dine-in
                            </div>
                            <div className="text-xs text-purple-700" data-id="element-1079">
                              {tableNumber ? `Table ${tableNumber}` : 'Ordering for your table'}
                            </div>
                          </div>
                        </div> : <>
                          <div className={`grid gap-2 mb-4 ${[canTakeout, canOrderAhead, canDeliver].filter(Boolean).length === 1 ? 'grid-cols-1' : [canTakeout, canOrderAhead, canDeliver].filter(Boolean).length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`} data-id="element-1080">
                            {canTakeout && <button onClick={() => setOrderMethod('takeout')} className={`rounded-xl border-2 py-3 px-3 text-center transition-colors text-sm font-semibold ${orderMethod === 'takeout' ? 'border-orange-300 bg-orange-50 text-gray-900' : 'border-gray-200 bg-white text-gray-500'}`} data-id="element-1081">
                                Takeout
                              </button>}
                            {canOrderAhead && <button onClick={() => setOrderMethod('orderAhead')} className={`rounded-xl border-2 py-3 px-3 text-center transition-colors text-sm font-semibold ${orderMethod === 'orderAhead' ? 'border-orange-300 bg-orange-50 text-gray-900' : 'border-gray-200 bg-white text-gray-500'}`} data-id="element-1082">
                                Order Ahead
                              </button>}
                            {canDeliver && <button onClick={() => setOrderMethod('delivery')} className={`rounded-xl border-2 py-3 px-3 text-center transition-colors text-sm font-semibold ${orderMethod === 'delivery' ? 'border-orange-300 bg-orange-50 text-gray-900' : 'border-gray-200 bg-white text-gray-500'}`} data-id="element-1083">
                                Delivery
                              </button>}
                          </div>

                          <AnimatePresence mode="wait" data-id="element-1084">
                            {(orderMethod === 'takeout' && canTakeout || orderMethod === 'orderAhead' && canOrderAhead) && <motion.div key="pickup" initial={{
                      opacity: 0,
                      height: 0
                    }} animate={{
                      opacity: 1,
                      height: 'auto'
                    }} exit={{
                      opacity: 0,
                      height: 0
                    }} className="overflow-hidden" data-id="element-1085">
                                <PickupSchedule mode={pickupMode} setMode={setPickupMode} dateIdx={pickupDateIdx} setDateIdx={setPickupDateIdx} time={pickupTime} setTime={setPickupTime} data-id="element-1086" />
                              </motion.div>}
                            {orderMethod === 'delivery' && canDeliver && <motion.div key="delivery" initial={{
                      opacity: 0,
                      height: 0
                    }} animate={{
                      opacity: 1,
                      height: 'auto'
                    }} exit={{
                      opacity: 0,
                      height: 0
                    }} className="overflow-hidden" data-id="element-1087">
                                <DeliveryLocation address={deliveryAddress} setAddress={setDeliveryAddress} gps={deliveryGPS} setGPS={setDeliveryGPS} isLocating={isLocating} setIsLocating={setIsLocating} data-id="element-1088" />
                              </motion.div>}
                          </AnimatePresence>
                        </>}
                    </div>

                    {/* Custom Questions */}
                    {applicableQuestions.length > 0 && <div className="bg-white border border-gray-100 rounded-xl p-4" data-id="element-1089">
                        <h3 className="font-bold text-gray-900 text-sm mb-3" data-id="element-1090">
                          A few questions
                        </h3>
                        <div className="space-y-4" data-id="element-1091">
                          {applicableQuestions.map(q => <div key={q.id} data-id="element-1092">
                              <label className="block font-bold text-gray-900 text-sm mb-1" data-id="element-1093">
                                {q.question}
                                {q.required && <span className="text-rose-500 ml-1" data-id="element-1094">*</span>}
                              </label>
                              {q.helpText && <p className="text-xs text-gray-500 mb-2" data-id="element-1095">
                                  {q.helpText}
                                </p>}

                              {q.type === 'text' && <input type="text" value={answers[q.id] as string || ''} onChange={e => setAnswers({
                      ...answers,
                      [q.id]: e.target.value
                    })} className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500" placeholder="Your answer..." data-id="element-1096" />}

                              {q.type === 'single-select' && q.options && <div className="space-y-2" data-id="element-1097">
                                  {q.options.map(opt => <label key={opt} className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 bg-white cursor-pointer hover:border-gray-300 transition-colors" data-id="element-1098">
                                      <input type="radio" name={q.id} value={opt} checked={answers[q.id] === opt} onChange={() => setAnswers({
                          ...answers,
                          [q.id]: opt
                        })} className="w-4 h-4 text-orange-500 focus:ring-orange-500 border-gray-300" data-id="element-1099" />
                                      <span className="text-sm font-medium text-gray-900" data-id="element-1100">
                                        {opt}
                                      </span>
                                    </label>)}
                                </div>}

                              {q.type === 'multi-select' && q.options && <div className="space-y-2" data-id="element-1101">
                                  {q.options.map(opt => {
                        const currentAns = answers[q.id] as string[] || [];
                        const isChecked = currentAns.includes(opt);
                        return <label key={opt} className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 bg-white cursor-pointer hover:border-gray-300 transition-colors" data-id="element-1102">
                                        <input type="checkbox" checked={isChecked} onChange={e => {
                            if (e.target.checked) {
                              setAnswers({
                                ...answers,
                                [q.id]: [...currentAns, opt]
                              });
                            } else {
                              setAnswers({
                                ...answers,
                                [q.id]: currentAns.filter(x => x !== opt)
                              });
                            }
                          }} className="w-4 h-4 rounded text-orange-500 focus:ring-orange-500 border-gray-300" data-id="element-1103" />
                                        <span className="text-sm font-medium text-gray-900" data-id="element-1104">
                                          {opt}
                                        </span>
                                      </label>;
                      })}
                                </div>}
                            </div>)}
                        </div>
                      </div>}

                    {/* Tip section */}
                    {effectiveTipping.customerTippingEnabled && <div className="bg-white border border-gray-100 rounded-xl p-4" data-id="element-1105">
                        <h3 className="font-bold text-gray-900 text-sm mb-3" data-id="element-1106">
                          Add a tip{' '}
                          <span className="text-gray-400 font-normal" data-id="element-1107">
                            · optional
                          </span>
                        </h3>
                        <TipSection tipMode={tipMode} setTipMode={setTipMode} tipPresetIdx={tipPresetIdx} setTipPresetIdx={setTipPresetIdx} customTipInput={customTipInput} setCustomTipInput={setCustomTipInput} presets={effectiveTipping.suggestedTipPcts} subtotal={subtotal} tipAmount={tipAmount} data-id="element-1108" />
                      </div>}
                  </>}
              </main>

              <div className="absolute left-0 right-0 bottom-0 px-4 py-3 bg-white border-t border-gray-200 space-y-2" data-id="element-1109">
                {hasOnlineMethods && !canOfferOffPremiseFallback ? <>
                    <button onClick={() => setSheetOpen(true)} disabled={orderMethod === 'delivery' && !deliveryAddress.trim() || missingRequiredQuestions || orderMethod === 'orderAhead' && pickupMode === 'scheduled' && !pickupTime} className="w-full py-3.5 rounded-xl bg-[#0f172a] text-white font-bold text-sm hover:bg-[#1e293b] transition-colors disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed" data-id="element-1110">
                      Continue to payment · {formatETB(total)}
                    </button>
                    <button onClick={() => setStep('cart')} className="w-full py-3 rounded-xl border border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors" data-id="element-1111">
                      Cancel
                    </button>
                  </> : <button onClick={() => navigate('/menu')} className="w-full py-3.5 rounded-xl bg-[#0f172a] text-white font-bold text-sm hover:bg-[#1e293b] transition-colors" data-id="element-1112">
                    Back to menu
                  </button>}
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Place Order bottom sheet */}
        <PlaceOrderSheet open={sheetOpen} onClose={() => setSheetOpen(false)} subtotal={subtotal} tax={tax} service={service} tipAmount={tipAmount} total={finalTotal} orderMethod={orderMethod} setOrderMethod={setOrderMethod} deliveryAddress={deliveryAddress} paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod} acceptedPaymentMethods={branch?.acceptedPaymentMethods} onPlaceOrder={handlePlaceOrder} tipEnabled={effectiveTipping.customerTippingEnabled} presets={effectiveTipping.suggestedTipPcts} tipMode={tipMode} setTipMode={setTipMode} tipPresetIdx={tipPresetIdx} setTipPresetIdx={setTipPresetIdx} promoCodeInput={promoCodeInput} setPromoCodeInput={setPromoCodeInput} appliedPromo={appliedPromo} promoError={promoError} handleApplyPromo={handleApplyPromo} handleRemovePromo={handleRemovePromo} discountAmount={discountAmount} bankAccounts={branch?.bankAccounts} bankTransferRef={bankTransferRef} setBankTransferRef={setBankTransferRef} selectedBankAccountId={selectedBankAccountId} setSelectedBankAccountId={setSelectedBankAccountId} orderRef={orderRef} hasOnlineMethods={hasOnlineMethods} isTableContext={isTableContext} customer={customer} data-id="element-1113" />

        {/* Bottom Tab Bar (matches the desktop screenshot's nav) */}
      </div>

      {/* Floating tabs on the right (only visible md+) — mirrors the screenshot */}
    </div>;
}
function SummaryRow({
  label,
  value,
  valueClass
}: {
  label: React.ReactNode;
  value: number;
  valueClass?: string;
}) {
  return <div className="flex items-center justify-between py-1.5 text-sm" data-id="element-1114">
      <span className="text-gray-600" data-id="element-1115">{label}</span>
      <span className={`tabular-nums ${valueClass || 'text-gray-900'}`} data-id="element-1116">
        {formatETB(value)}
      </span>
    </div>;
}
function TipSection({
  tipMode,
  setTipMode,
  tipPresetIdx,
  setTipPresetIdx,
  customTipInput,
  setCustomTipInput,
  presets,
  subtotal,
  tipAmount
}: {
  tipMode: TipMode;
  setTipMode: (m: TipMode) => void;
  tipPresetIdx: number;
  setTipPresetIdx: (i: number) => void;
  customTipInput: string;
  setCustomTipInput: (s: string) => void;
  presets: [number, number, number];
  subtotal: number;
  tipAmount: number;
}) {
  return <motion.div layout className="bg-white border border-gray-100 rounded-xl p-4" data-id="element-1117">
      <div className="flex items-center justify-between mb-1" data-id="element-1118">
        <div className="flex items-center gap-2" data-id="element-1119">
          <div className="w-7 h-7 rounded-md bg-emerald-100 text-emerald-600 flex items-center justify-center" data-id="element-1120">
            <HandCoins size={14} data-id="element-1121" />
          </div>
          <div data-id="element-1122">
            <h2 className="font-bold text-gray-900 text-sm leading-tight" data-id="element-1123">
              Add a tip
            </h2>
            <p className="text-[11px] text-gray-500 leading-tight" data-id="element-1124">
              100% goes to the team
            </p>
          </div>
        </div>
        <AnimatePresence data-id="element-1125">
          {tipAmount > 0 && <motion.span key={tipAmount} initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0
        }} className="text-sm font-bold text-emerald-700 tabular-nums" data-id="element-1126">
              + {formatETB(tipAmount)}
            </motion.span>}
        </AnimatePresence>
      </div>

      {/* Preset chips */}
      <div className="grid grid-cols-4 gap-2 mt-3" data-id="element-1127">
        {presets.map((pct, idx) => {
        const isActive = tipMode === 'preset' && tipPresetIdx === idx;
        const amount = subtotal * pct / 100;
        return <button key={idx} onClick={() => {
          setTipMode('preset');
          setTipPresetIdx(idx);
        }} className={`relative rounded-xl border-2 py-2.5 px-2 text-center transition-all ${isActive ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-1128">
              <div className={`text-base font-bold ${isActive ? 'text-emerald-700' : 'text-gray-900'}`} data-id="element-1129">
                {pct}%
              </div>
              <div className="text-[10px] text-gray-500 tabular-nums mt-0.5" data-id="element-1130">
                {formatETB(amount)}
              </div>
              {isActive && <motion.div layoutId="tip-check" className="absolute top-1 right-1" data-id="element-1131">
                  <CheckCircle2 size={12} className="text-emerald-500 fill-emerald-100" data-id="element-1132" />
                </motion.div>}
            </button>;
      })}

        {/* Custom chip */}
        <button onClick={() => setTipMode('custom')} className={`relative rounded-xl border-2 py-2.5 px-2 text-center transition-all ${tipMode === 'custom' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-1133">
          <div className={`text-base font-bold ${tipMode === 'custom' ? 'text-emerald-700' : 'text-gray-900'}`} data-id="element-1134">
            Custom
          </div>
          <div className="text-[10px] text-gray-500 mt-0.5" data-id="element-1135">Any amount</div>
          {tipMode === 'custom' && <motion.div layoutId="tip-check" className="absolute top-1 right-1" data-id="element-1136">
              <CheckCircle2 size={12} className="text-emerald-500 fill-emerald-100" data-id="element-1137" />
            </motion.div>}
        </button>
      </div>

      <AnimatePresence initial={false} data-id="element-1138">
        {tipMode === 'custom' && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2
      }} className="overflow-hidden" data-id="element-1139">
            <div className="mt-3" data-id="element-1140">
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5" data-id="element-1141">
                Custom tip amount
              </label>
              <div className="relative" data-id="element-1142">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-gray-500" data-id="element-1143">
                  ETB
                </span>
                <input type="number" min={0} step={1} value={customTipInput} onChange={e => setCustomTipInput(e.target.value)} placeholder="0" autoFocus className="full width pl-12 pr-3 py-2.5 rounded-lg border border-gray-200 text-base font-bold tabular-nums bg-white focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 transition" data-id="element-1144" />
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* No tip option */}
      <button onClick={() => setTipMode('none')} className={`w-full mt-2 py-2 rounded-lg text-xs font-semibold transition-colors ${tipMode === 'none' ? 'text-gray-500' : 'text-gray-400 hover:text-gray-600'}`} data-id="element-1145">
        {tipMode === 'none' ? '✓ No tip selected' : 'No tip, thanks'}
      </button>
    </motion.div>;
}
function PlaceOrderSheet({
  open,
  onClose,
  subtotal,
  tipAmount,
  total,
  orderMethod,
  deliveryAddress,
  paymentMethod,
  setPaymentMethod,
  acceptedPaymentMethods,
  onPlaceOrder,
  tipEnabled,
  tipMode,
  presets,
  setTipMode,
  setTipPresetIdx,
  promoCodeInput,
  setPromoCodeInput,
  appliedPromo,
  promoError,
  handleApplyPromo,
  handleRemovePromo,
  discountAmount,
  bankAccounts,
  bankTransferRef,
  setBankTransferRef,
  selectedBankAccountId,
  setSelectedBankAccountId,
  orderRef,
  hasOnlineMethods,
  isTableContext,
  customer
}: {
  open: boolean;
  onClose: () => void;
  subtotal: number;
  tipAmount: number;
  total: number;
  orderMethod: 'takeout' | 'orderAhead' | 'delivery' | 'table';
  deliveryAddress: string;
  paymentMethod: PaymentMethod;
  setPaymentMethod: (m: PaymentMethod) => void;
  acceptedPaymentMethods: Record<PaymentMethod, boolean> | undefined;
  onPlaceOrder: () => void;
  tipEnabled: boolean;
  tipMode: TipMode;
  presets: [number, number, number];
  setTipMode: (m: TipMode) => void;
  setTipPresetIdx: (i: number) => void;
  promoCodeInput: string;
  setPromoCodeInput: (v: string) => void;
  appliedPromo: Promotion | null;
  promoError: string | null;
  handleApplyPromo: () => void;
  handleRemovePromo: () => void;
  discountAmount: number;
  bankAccounts?: {
    id: string;
    bankName: string;
    accountName: string;
    accountNumber: string;
    label?: string;
    active: boolean;
  }[];
  bankTransferRef: string;
  setBankTransferRef: (v: string) => void;
  selectedBankAccountId?: string;
  setSelectedBankAccountId: (v: string) => void;
  orderRef: string;
  hasOnlineMethods: boolean;
  isTableContext: boolean;
  customer: any;
}) {
  const [notified, setNotified] = useState(false);
  const {
    captureLead
  } = useCRM();
  const handleNotifyMe = () => {
    if (customer?.phone) {
      captureLead(customer.phone, {
        marketingOptIn: true,
        notes: 'Requested notification for online ordering availability.'
      });
    }
    setNotified(true);
  };
  return <AnimatePresence data-id="element-1146">
      {open && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 0.5
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-black z-20" data-id="element-1147" />
          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        stiffness: 380,
        damping: 38
      }} className="absolute left-0 right-0 bottom-0 bg-white rounded-t-3xl shadow-2xl z-30 max-h-[90dvh] overflow-y-auto overscroll-contain" data-id="element-1148">
            {/* Handle */}
            <div className="flex justify-center pt-2 pb-1" data-id="element-1149">
              <div className="w-10 h-1 rounded-full bg-gray-300" data-id="element-1150" />
            </div>

            {!hasOnlineMethods ? <div className="px-6 py-8 text-center" data-id="element-1151">
                <div className="w-16 h-16 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1152">
                  <MapPin size={28} data-id="element-1153" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1154">
                  {isTableContext ? "Ordering from your table isn't available here" : "Online ordering isn't available at this location yet"}
                </h2>
                <p className="text-sm text-gray-500 mb-6" data-id="element-1155">
                  Please order at the counter. We've saved your profile for next
                  time!
                </p>

                {customer?.name && <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-100 flex items-center gap-3 text-left" data-id="element-1156">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center font-bold text-gray-600" data-id="element-1157">
                      {customer.name.charAt(0)}
                    </div>
                    <div data-id="element-1158">
                      <div className="font-bold text-gray-900 text-sm" data-id="element-1159">
                        {customer.name}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-1160">
                        {customer.phone}
                      </div>
                    </div>
                  </div>}

                {notified ? <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="bg-emerald-50 text-emerald-700 p-4 rounded-xl font-bold flex items-center justify-center gap-2" data-id="element-1161">
                    <CheckCircle2 size={18} data-id="element-1162" />
                    You're on the list!
                  </motion.div> : <button onClick={handleNotifyMe} className="w-full py-3.5 rounded-xl bg-[#0f172a] text-white font-bold text-sm hover:bg-[#1e293b] transition-colors" data-id="element-1163">
                    Notify me when ordering launches here
                  </button>}

                <button onClick={onClose} className="w-full mt-3 py-3 rounded-xl border border-gray-200 text-gray-700 font-bold text-sm hover:bg-gray-50 transition-colors" data-id="element-1164">
                  Back to menu
                </button>
              </div> : <>
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100" data-id="element-1165">
                  <h2 className="text-lg font-bold text-gray-900" data-id="element-1166">
                    Place Order
                  </h2>
                  <button onClick={onClose} className="p-1 -mr-1 text-gray-400 hover:text-gray-700" aria-label="Close" data-id="element-1167">
                    <X size={20} data-id="element-1168" />
                  </button>
                </div>

                <div className="px-4 py-3 space-y-4" data-id="element-1169">
                  {/* Order Summary card */}
                  {/* Compact total — the cart screen already showed the full
                   breakdown. Here we only confirm the final amount the
                   customer is about to pay, with the live tip surfacing
                   when one is selected inside this sheet. */}
                  <div className="bg-gray-50 rounded-xl px-3.5 py-3 border border-gray-100" data-id="element-1170">
                    <AnimatePresence initial={false} data-id="element-1171">
                      {appliedPromo && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} className="overflow-hidden" data-id="element-1172">
                          <div className="flex items-center justify-between text-xs pb-2 mb-2 border-b border-gray-200" data-id="element-1173">
                            <span className="inline-flex items-center gap-1.5 text-orange-600 font-semibold" data-id="element-1174">
                              <Ticket size={12} data-id="element-1175" />
                              Discount ({appliedPromo.code})
                            </span>
                            <div className="flex items-center gap-2" data-id="element-1176">
                              <span className="tabular-nums text-orange-600 font-semibold" data-id="element-1177">
                                − {formatETB(discountAmount)}
                              </span>
                              <button onClick={handleRemovePromo} className="p-0.5 text-gray-400 hover:text-red-500 rounded" data-id="element-1178">
                                <X size={12} data-id="element-1179" />
                              </button>
                            </div>
                          </div>
                        </motion.div>}
                      {tipAmount > 0 && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} className="overflow-hidden" data-id="element-1180">
                          <div className="flex items-center justify-between text-xs pb-2 mb-2 border-b border-gray-200" data-id="element-1181">
                            <span className="inline-flex items-center gap-1.5 text-emerald-700 font-semibold" data-id="element-1182">
                              <HandCoins size={12} data-id="element-1183" />
                              Tip added
                            </span>
                            <span className="tabular-nums text-emerald-700 font-semibold" data-id="element-1184">
                              + {formatETB(tipAmount)}
                            </span>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                    <div className="flex items-center justify-between" data-id="element-1185">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500" data-id="element-1186">
                        You'll pay
                      </span>
                      <motion.span key={total} initial={{
                  scale: 0.95
                }} animate={{
                  scale: 1
                }} className="font-bold text-gray-900 tabular-nums text-lg" data-id="element-1187">
                        {formatETB(total)}
                      </motion.span>
                    </div>
                  </div>

                  {/* Promo Code Input */}
                  {!appliedPromo && <div className="flex items-start gap-2" data-id="element-1188">
                      <div className="flex-1" data-id="element-1189">
                        <div className="relative" data-id="element-1190">
                          <Ticket size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-1191" />
                          <input type="text" placeholder="Promo code" value={promoCodeInput} onChange={e => setPromoCodeInput(e.target.value.toUpperCase())} className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 uppercase" data-id="element-1192" />
                        </div>
                        {promoError && <p className="text-xs text-red-500 mt-1.5 ml-1 font-medium" data-id="element-1193">
                            {promoError}
                          </p>}
                      </div>
                      <button onClick={handleApplyPromo} disabled={!promoCodeInput.trim()} className="px-4 py-2.5 bg-gray-900 text-white rounded-xl text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-800 transition-colors" data-id="element-1194">
                        Apply
                      </button>
                    </div>}

                  {/* Last-chance tip nudge — only when tipping is enabled and
                   the customer skipped it on the Details screen. Soft,
                   non-blocking, one-tap to add. */}
                  {tipEnabled && tipMode === 'none' && <motion.div initial={{
              opacity: 0,
              y: -6
            }} animate={{
              opacity: 1,
              y: 0
            }} className="bg-emerald-50 border border-emerald-100 rounded-xl p-3" data-id="element-1195">
                      <div className="flex items-center gap-2 mb-2" data-id="element-1196">
                        <Sparkles size={14} className="text-emerald-600" data-id="element-1197" />
                        <span className="font-bold text-emerald-900 text-sm" data-id="element-1198">
                          Add a tip for the team?
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2" data-id="element-1199">
                        {presets.map((pct, idx) => <button key={idx} onClick={() => {
                  setTipMode('preset');
                  setTipPresetIdx(idx);
                }} className="rounded-lg border border-emerald-200 bg-white py-2 text-center hover:border-emerald-400 hover:bg-emerald-50 transition-colors" data-id="element-1200">
                            <div className="text-sm font-bold text-emerald-700" data-id="element-1201">
                              {pct}%
                            </div>
                            <div className="text-[10px] text-emerald-700/70 tabular-nums" data-id="element-1202">
                              {formatETB(subtotal * pct / 100)}
                            </div>
                          </button>)}
                      </div>
                    </motion.div>}

                  {/* Payment method */}
                  <div data-id="element-1203">
                    <h3 className="text-sm font-bold text-gray-900 mb-2" data-id="element-1204">
                      Payment method
                    </h3>
                    <div className="space-y-2" data-id="element-1205">
                      {acceptedPaymentMethods?.telebirr && <button type="button" onClick={() => setPaymentMethod('telebirr')} className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-colors text-left ${paymentMethod === 'telebirr' ? 'border-orange-300 bg-orange-50/50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1206">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${paymentMethod === 'telebirr' ? 'border-orange-500' : 'border-gray-300'}`} data-id="element-1207">
                            {paymentMethod === 'telebirr' && <div className="w-2 h-2 rounded-full bg-orange-500" data-id="element-1208" />}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-1209">
                            <div className="text-sm font-bold text-gray-900" data-id="element-1210">
                              Telebirr
                            </div>
                            <div className="text-[11px] text-gray-500" data-id="element-1211">
                              Pay via app or USSD
                            </div>
                          </div>
                          <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0" data-id="element-1212">
                            <Smartphone size={14} data-id="element-1213" />
                          </div>
                        </button>}
                      {acceptedPaymentMethods?.['cbe-birr'] && <button type="button" onClick={() => setPaymentMethod('cbe-birr')} className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-colors text-left ${paymentMethod === 'cbe-birr' ? 'border-orange-300 bg-orange-50/50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1214">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${paymentMethod === 'cbe-birr' ? 'border-orange-500' : 'border-gray-300'}`} data-id="element-1215">
                            {paymentMethod === 'cbe-birr' && <div className="w-2 h-2 rounded-full bg-orange-500" data-id="element-1216" />}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-1217">
                            <div className="text-sm font-bold text-gray-900" data-id="element-1218">
                              CBE Birr
                            </div>
                            <div className="text-[11px] text-gray-500" data-id="element-1219">
                              Direct bank transfer
                            </div>
                          </div>
                          <div className="w-7 h-7 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center flex-shrink-0" data-id="element-1220">
                            <Building2 size={14} data-id="element-1221" />
                          </div>
                        </button>}
                      {acceptedPaymentMethods?.card && <button type="button" onClick={() => setPaymentMethod('card')} className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-colors text-left ${paymentMethod === 'card' ? 'border-orange-300 bg-orange-50/50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1222">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${paymentMethod === 'card' ? 'border-orange-500' : 'border-gray-300'}`} data-id="element-1223">
                            {paymentMethod === 'card' && <div className="w-2 h-2 rounded-full bg-orange-500" data-id="element-1224" />}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-1225">
                            <div className="text-sm font-bold text-gray-900" data-id="element-1226">
                              Card
                            </div>
                            <div className="text-[11px] text-gray-500" data-id="element-1227">
                              Visa, Mastercard
                            </div>
                          </div>
                          <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0" data-id="element-1228">
                            <CreditCard size={14} data-id="element-1229" />
                          </div>
                        </button>}
                      {acceptedPaymentMethods?.cash && <button type="button" onClick={() => setPaymentMethod('cash')} className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-colors text-left ${paymentMethod === 'cash' ? 'border-orange-300 bg-orange-50/50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1230">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${paymentMethod === 'cash' ? 'border-orange-500' : 'border-gray-300'}`} data-id="element-1231">
                            {paymentMethod === 'cash' && <div className="w-2 h-2 rounded-full bg-orange-500" data-id="element-1232" />}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-1233">
                            <div className="text-sm font-bold text-gray-900" data-id="element-1234">
                              Cash
                            </div>
                            <div className="text-[11px] text-gray-500" data-id="element-1235">
                              Pay at counter
                            </div>
                          </div>
                          <div className="w-7 h-7 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0" data-id="element-1236">
                            <Banknote size={14} data-id="element-1237" />
                          </div>
                        </button>}
                      {acceptedPaymentMethods?.['bank-transfer'] && <button type="button" onClick={() => setPaymentMethod('bank-transfer')} className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-colors text-left ${paymentMethod === 'bank-transfer' ? 'border-orange-300 bg-orange-50/50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1238">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${paymentMethod === 'bank-transfer' ? 'border-orange-500' : 'border-gray-300'}`} data-id="element-1239">
                            {paymentMethod === 'bank-transfer' && <div className="w-2 h-2 rounded-full bg-orange-500" data-id="element-1240" />}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-1241">
                            <div className="text-sm font-bold text-gray-900" data-id="element-1242">
                              Bank Transfer
                            </div>
                            <div className="text-[11px] text-gray-500" data-id="element-1243">
                              Direct transfer to our account
                            </div>
                          </div>
                          <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center flex-shrink-0" data-id="element-1244">
                            <Building2 size={14} data-id="element-1245" />
                          </div>
                        </button>}
                    </div>
                  </div>

                  {/* Bank Transfer Instructions */}
                  <AnimatePresence data-id="element-1246">
                    {paymentMethod === 'bank-transfer' && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="overflow-hidden" data-id="element-1247">
                        <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 mt-4 space-y-4" data-id="element-1248">
                          {bankAccounts && bankAccounts.filter(a => a.active).length > 0 ? <>
                              <div className="space-y-2" data-id="element-1249">
                                <label className="block text-xs font-bold text-indigo-900 uppercase tracking-wide" data-id="element-1250">
                                  Select Bank
                                </label>
                                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide" data-id="element-1251">
                                  {bankAccounts.filter(a => a.active).map(account => <button key={account.id} onClick={() => setSelectedBankAccountId(account.id)} className={`px-3 py-1.5 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors border ${selectedBankAccountId === account.id ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-indigo-700 border-indigo-200 hover:border-indigo-300'}`} data-id="element-1252">
                                        {account.label || account.bankName}
                                      </button>)}
                                </div>
                              </div>

                              {selectedBankAccountId && <div className="bg-white rounded-lg p-3 border border-indigo-100 space-y-3" data-id="element-1253">
                                  {(() => {
                        const account = bankAccounts.find(a => a.id === selectedBankAccountId);
                        if (!account) return null;
                        return <>
                                        <div data-id="element-1254">
                                          <div className="text-[10px] text-gray-500 uppercase font-bold tracking-wider" data-id="element-1255">
                                            Bank
                                          </div>
                                          <div className="text-sm font-semibold text-gray-900" data-id="element-1256">
                                            {account.bankName}
                                          </div>
                                        </div>
                                        <div data-id="element-1257">
                                          <div className="text-[10px] text-gray-500 uppercase font-bold tracking-wider" data-id="element-1258">
                                            Account Name
                                          </div>
                                          <div className="text-sm font-semibold text-gray-900" data-id="element-1259">
                                            {account.accountName}
                                          </div>
                                        </div>
                                        <div className="flex items-center justify-between" data-id="element-1260">
                                          <div data-id="element-1261">
                                            <div className="text-[10px] text-gray-500 uppercase font-bold tracking-wider" data-id="element-1262">
                                              Account Number
                                            </div>
                                            <div className="text-base font-bold text-indigo-700 tabular-nums" data-id="element-1263">
                                              {account.accountNumber}
                                            </div>
                                          </div>
                                          <button onClick={() => navigator.clipboard.writeText(account.accountNumber)} className="px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-lg text-xs font-bold hover:bg-indigo-100 transition-colors" data-id="element-1264">
                                            Copy
                                          </button>
                                        </div>
                                      </>;
                      })()}
                                </div>}

                              <div className="bg-white rounded-lg p-3 border border-indigo-100" data-id="element-1265">
                                <div className="text-[10px] text-gray-500 uppercase font-bold tracking-wider mb-1" data-id="element-1266">
                                  Amount to Transfer
                                </div>
                                <div className="flex items-center justify-between" data-id="element-1267">
                                  <div className="text-xl font-extrabold text-gray-900 tabular-nums" data-id="element-1268">
                                    {formatETB(total)}
                                  </div>
                                  <button onClick={() => navigator.clipboard.writeText(total.toString())} className="px-3 py-1.5 bg-gray-50 text-gray-600 rounded-lg text-xs font-bold hover:bg-gray-100 transition-colors" data-id="element-1269">
                                    Copy
                                  </button>
                                </div>
                              </div>

                              <div className="bg-white rounded-lg p-3 border border-indigo-100" data-id="element-1270">
                                <div className="text-[10px] text-gray-500 uppercase font-bold tracking-wider mb-1" data-id="element-1271">
                                  Order Reference (Include in Memo)
                                </div>
                                <div className="flex items-center justify-between" data-id="element-1272">
                                  <div className="text-sm font-bold text-gray-900" data-id="element-1273">
                                    {orderRef}
                                  </div>
                                  <button onClick={() => navigator.clipboard.writeText(orderRef)} className="px-3 py-1.5 bg-gray-50 text-gray-600 rounded-lg text-xs font-bold hover:bg-gray-100 transition-colors" data-id="element-1274">
                                    Copy
                                  </button>
                                </div>
                              </div>

                              <div data-id="element-1275">
                                <label className="block text-xs font-bold text-indigo-900 uppercase tracking-wide mb-1.5" data-id="element-1276">
                                  Transaction ID / Reference
                                </label>
                                <input type="text" value={bankTransferRef} onChange={e => setBankTransferRef(e.target.value)} placeholder="Paste reference here..." className="w-full px-3 py-2.5 rounded-lg border border-indigo-200 text-sm focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400" data-id="element-1277" />
                              </div>
                            </> : <div className="text-sm text-indigo-800 text-center py-2" data-id="element-1278">
                              No active bank accounts available.
                            </div>}
                        </div>
                      </motion.div>}
                  </AnimatePresence>

                  {/* Actions */}
                  <div className="grid grid-cols-2 gap-2 pt-1" data-id="element-1279">
                    <button onClick={onClose} className="py-3 rounded-xl border border-gray-200 text-gray-700 font-bold text-sm hover:bg-gray-50 transition-colors" data-id="element-1280">
                      Cancel
                    </button>
                    <button onClick={onPlaceOrder} disabled={orderMethod === 'delivery' && !deliveryAddress.trim()} className="py-3 rounded-xl bg-[#0f172a] text-white font-bold text-sm hover:bg-[#1e293b] transition-colors disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed" data-id="element-1281">
                      Confirm Order
                    </button>
                  </div>

                  <div className="text-[10px] text-center text-gray-400 leading-snug pb-2" data-id="element-1282">
                    {paymentMethod === 'telebirr' && <>
                        You'll be redirected to Telebirr to complete payment of{' '}
                        {formatETB(total)}.
                      </>}
                    {paymentMethod === 'cbe-birr' && <>
                        You'll be redirected to CBE Birr to complete payment of{' '}
                        {formatETB(total)}.
                      </>}
                    {paymentMethod === 'card' && <>
                        A secure card payment for {formatETB(total)} will be
                        processed on Confirm.
                      </>}
                    {paymentMethod === 'cash' && <>
                        Cash payment of {formatETB(total)} due at the counter.
                      </>}
                    {paymentMethod === 'bank-transfer' && <>
                        Payment under review — we'll confirm shortly after you
                        place the order.
                      </>}
                  </div>
                </div>
              </>}
          </motion.div>
        </>}
    </AnimatePresence>;
}
function PickupSchedule({
  mode,
  setMode,
  dateIdx,
  setDateIdx,
  time,
  setTime
}: {
  mode: 'asap' | 'scheduled';
  setMode: (m: 'asap' | 'scheduled') => void;
  dateIdx: number;
  setDateIdx: (i: number) => void;
  time: string | null;
  setTime: (t: string | null) => void;
}) {
  // Build 5 days starting today
  const dates = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Array.from({
      length: 5
    }).map((_, i) => {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      return d;
    });
  }, []);
  const dateLabel = (d: Date, i: number) => {
    if (i === 0) return 'Today';
    if (i === 1) return 'Tomorrow';
    return d.toLocaleDateString(undefined, {
      weekday: 'short'
    });
  };
  // Time slots — every 15 min, but only future-ish slots
  const slots = useMemo(() => {
    const out: string[] = [];
    const start = dateIdx === 0 ? new Date() : new Date(dates[dateIdx]);
    if (dateIdx === 0) {
      // round up to next 15-min, add 30 min lead time
      start.setMinutes(start.getMinutes() + 30);
      const m = start.getMinutes();
      start.setMinutes(Math.ceil(m / 15) * 15, 0, 0);
    } else {
      start.setHours(8, 0, 0, 0);
    }
    const end = new Date(dates[dateIdx]);
    end.setHours(23, 0, 0, 0);
    const cursor = new Date(start);
    while (cursor <= end) {
      out.push(cursor.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      }));
      cursor.setMinutes(cursor.getMinutes() + 15);
    }
    return out;
  }, [dateIdx, dates]);
  return <div className="rounded-xl border border-orange-200 bg-orange-50/50 p-3.5 space-y-3" data-id="element-1283">
      <div className="flex items-center gap-2" data-id="element-1284">
        <div className="w-7 h-7 rounded-md bg-orange-100 text-orange-600 flex items-center justify-center" data-id="element-1285">
          <Calendar size={14} data-id="element-1286" />
        </div>
        <div className="flex-1" data-id="element-1287">
          <h3 className="font-bold text-gray-900 text-sm leading-tight" data-id="element-1288">
            When should we have it ready?
          </h3>
          <p className="text-[11px] text-gray-500 leading-tight" data-id="element-1289">
            Pickup at the branch
          </p>
        </div>
      </div>

      {/* ASAP vs Schedule */}
      <div className="grid grid-cols-2 gap-2" data-id="element-1290">
        <button onClick={() => setMode('asap')} className={`rounded-lg border-2 py-2.5 px-3 text-center transition-colors text-sm font-semibold flex items-center justify-center gap-1.5 ${mode === 'asap' ? 'border-orange-500 bg-white text-gray-900' : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'}`} data-id="element-1291">
          <Zap size={13} className={mode === 'asap' ? 'text-orange-500' : ''} data-id="element-1292" />
          ASAP
          <span className="text-[10px] font-normal text-gray-400" data-id="element-1293">~25 min</span>
        </button>
        <button onClick={() => setMode('scheduled')} className={`rounded-lg border-2 py-2.5 px-3 text-center transition-colors text-sm font-semibold flex items-center justify-center gap-1.5 ${mode === 'scheduled' ? 'border-orange-500 bg-white text-gray-900' : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'}`} data-id="element-1294">
          <Clock size={13} className={mode === 'scheduled' ? 'text-orange-500' : ''} data-id="element-1295" />
          Schedule
        </button>
      </div>

      <AnimatePresence initial={false} data-id="element-1296">
        {mode === 'scheduled' && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2
      }} className="overflow-hidden" data-id="element-1297">
            <div className="space-y-3 pt-1" data-id="element-1298">
              {/* Date selector */}
              <div data-id="element-1299">
                <div className="text-[10px] uppercase tracking-wide font-semibold text-gray-500 mb-1.5" data-id="element-1300">
                  Date
                </div>
                <div className="grid grid-cols-5 gap-1.5" data-id="element-1301">
                  {dates.map((d, i) => {
                const isActive = dateIdx === i;
                return <button key={i} onClick={() => {
                  setDateIdx(i);
                  setTime(null);
                }} className={`rounded-lg border-2 py-2 px-1 text-center transition-colors ${isActive ? 'border-orange-500 bg-orange-50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-1302">
                        <div className={`text-[10px] font-semibold ${isActive ? 'text-orange-700' : 'text-gray-500'} leading-none`} data-id="element-1303">
                          {dateLabel(d, i)}
                        </div>
                        <div className={`text-base font-bold mt-1 leading-none ${isActive ? 'text-gray-900' : 'text-gray-900'}`} data-id="element-1304">
                          {d.getDate()}
                        </div>
                        <div className="text-[9px] text-gray-400 mt-0.5 leading-none uppercase" data-id="element-1305">
                          {d.toLocaleDateString(undefined, {
                      month: 'short'
                    })}
                        </div>
                      </button>;
              })}
                </div>
              </div>

              {/* Time selector */}
              <div data-id="element-1306">
                <div className="text-[10px] uppercase tracking-wide font-semibold text-gray-500 mb-1.5" data-id="element-1307">
                  Time
                </div>
                {slots.length === 0 ? <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 flex items-start gap-2.5" data-id="element-1308">
                    <Clock size={14} className="text-orange-500 flex-shrink-0 mt-0.5" data-id="element-1309" />
                    <div className="flex-1 min-w-0" data-id="element-1310">
                      <p className="text-xs font-semibold text-orange-900 leading-snug" data-id="element-1311">
                        Too close to closing for{' '}
                        {dateIdx === 0 ? 'today' : 'this day'}
                      </p>
                      <p className="text-[11px] text-orange-700/80 leading-snug mt-0.5" data-id="element-1312">
                        We need at least 30 minutes to prep your order. Try a
                        later day.
                      </p>
                      {dateIdx < dates.length - 1 && <button type="button" onClick={() => {
                  setDateIdx(dateIdx + 1);
                  setTime(null);
                }} className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-orange-700 hover:text-orange-800 transition-colors" data-id="element-1313">
                          Switch to{' '}
                          {dateIdx + 1 === 1 ? 'Tomorrow' : dates[dateIdx + 1].toLocaleDateString(undefined, {
                    weekday: 'long'
                  })}
                          <span aria-hidden="true" data-id="element-1314">→</span>
                        </button>}
                    </div>
                  </div> : <div className="max-h-32 overflow-y-auto" data-id="element-1315">
                    <div className="grid grid-cols-4 gap-1.5 pr-1" data-id="element-1316">
                      {slots.map(t => {
                  const isActive = time === t;
                  return <button key={t} onClick={() => setTime(t)} className={`rounded-md border py-1.5 text-center text-xs font-semibold tabular-nums transition-colors ${isActive ? 'border-orange-500 bg-orange-500 text-white' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'}`} data-id="element-1317">
                            {t}
                          </button>;
                })}
                    </div>
                  </div>}
              </div>

              {time && <div className="flex items-center gap-2 text-xs font-semibold text-orange-700" data-id="element-1318">
                  <CheckCircle2 size={13} data-id="element-1319" />
                  Pickup {dateLabel(dates[dateIdx], dateIdx).toLowerCase()} at {time}
                </div>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function DeliveryLocation({
  address,
  setAddress,
  gps,
  setGPS,
  isLocating,
  setIsLocating
}: {
  address: string;
  setAddress: (s: string) => void;
  gps: {
    lat: number;
    lng: number;
  } | null;
  setGPS: (p: {
    lat: number;
    lng: number;
  } | null) => void;
  isLocating: boolean;
  setIsLocating: (b: boolean) => void;
}) {
  const handleDropPin = () => {
    setIsLocating(true);
    // Simulate geolocation lookup. Default to a believable Addis Ababa coord
    // with a small jitter so re-pin feels real.
    window.setTimeout(() => {
      const baseLat = 9.0084;
      const baseLng = 38.7575;
      const jitter = () => (Math.random() - 0.5) * 0.02;
      setGPS({
        lat: +(baseLat + jitter()).toFixed(5),
        lng: +(baseLng + jitter()).toFixed(5)
      });
      setIsLocating(false);
    }, 900);
  };
  return <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-3.5 space-y-3" data-id="element-1320">
      <div className="flex items-center gap-2" data-id="element-1321">
        <div className="w-7 h-7 rounded-md bg-blue-100 text-blue-600 flex items-center justify-center" data-id="element-1322">
          <MapPin size={14} data-id="element-1323" />
        </div>
        <div className="flex-1" data-id="element-1324">
          <h3 className="font-bold text-gray-900 text-sm leading-tight" data-id="element-1325">
            Where should we deliver?
          </h3>
          <p className="text-[11px] text-gray-500 leading-tight" data-id="element-1326">
            Address and a GPS pin help the driver find you
          </p>
        </div>
      </div>

      {/* Address */}
      <div data-id="element-1327">
        <label className="block text-[10px] uppercase tracking-wide font-semibold text-gray-500 mb-1" data-id="element-1328">
          Delivery address
        </label>
        <textarea value={address} onChange={e => setAddress(e.target.value)} rows={2} placeholder="Building name, apartment, street, neighborhood, landmarks..." className="full width px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition resize-none" data-id="element-1329" />
      </div>

      {/* GPS pin */}
      <div data-id="element-1330">
        <div className="flex items-center justify-between mb-1" data-id="element-1331">
          <span className="text-[10px] uppercase tracking-wide font-semibold text-gray-500" data-id="element-1332">
            GPS pin
          </span>
          {gps && !isLocating && <span className="text-[10px] tabular-nums text-gray-500" data-id="element-1333">
              {gps.lat.toFixed(5)}, {gps.lng.toFixed(5)}
            </span>}
        </div>

        {!gps ? <button onClick={handleDropPin} disabled={isLocating} className="full width rounded-lg border-2 border-dashed border-blue-300 bg-white hover:bg-blue-50 transition-colors py-4 px-3 flex flex-col items-center justify-center gap-1.5 disabled:opacity-60 disabled:cursor-wait" data-id="element-1334">
            {isLocating ? <>
                <motion.div animate={{
            rotate: 360
          }} transition={{
            duration: 1,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-1335">
                  <Crosshair size={20} className="text-blue-500" data-id="element-1336" />
                </motion.div>
                <span className="text-sm font-semibold text-blue-700" data-id="element-1337">
                  Getting your location...
                </span>
              </> : <>
                <Navigation size={20} className="text-blue-500" data-id="element-1338" />
                <span className="text-sm font-semibold text-blue-700" data-id="element-1339">
                  Use my current location
                </span>
                <span className="text-[11px] text-gray-500" data-id="element-1340">
                  We'll drop a GPS pin for the driver
                </span>
              </>}
          </button> : <MiniMap gps={gps} onRepin={handleDropPin} onClear={() => setGPS(null)} isLocating={isLocating} data-id="element-1341" />}
      </div>
    </div>;
}
function MiniMap({
  gps,
  onRepin,
  onClear,
  isLocating
}: {
  gps: {
    lat: number;
    lng: number;
  };
  onRepin: () => void;
  onClear: () => void;
  isLocating: boolean;
}) {
  return <div className="rounded-lg overflow-hidden border-2 border-blue-300 bg-white" data-id="element-1342">
      {/* Faux map surface */}
      <div className="relative h-32" style={{
      backgroundImage: 'linear-gradient(135deg, #e0f2fe 0%, #ecfdf5 50%, #fef3c7 100%), repeating-linear-gradient(0deg, transparent 0 19px, rgba(15,23,42,0.07) 19px 20px), repeating-linear-gradient(90deg, transparent 0 19px, rgba(15,23,42,0.07) 19px 20px)',
      backgroundBlendMode: 'multiply'
    }} data-id="element-1343">
        {/* Streets */}
        <div className="absolute top-1/3 left-0 right-0 h-1.5 bg-white/80" data-id="element-1344" />
        <div className="absolute top-2/3 left-0 right-0 h-1 bg-white/60" data-id="element-1345" />
        <div className="absolute left-1/4 top-0 bottom-0 w-1.5 bg-white/80" data-id="element-1346" />
        <div className="absolute left-3/4 top-0 bottom-0 w-1 bg-white/60" data-id="element-1347" />

        {/* Pin */}
        <motion.div key={`${gps.lat}-${gps.lng}`} initial={{
        y: -16,
        opacity: 0,
        scale: 0.6
      }} animate={{
        y: 0,
        opacity: 1,
        scale: 1
      }} transition={{
        type: 'spring',
        stiffness: 380,
        damping: 18
      }} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-full" data-id="element-1348">
          <div className="relative" data-id="element-1349">
            <div className="w-7 h-7 rounded-full bg-blue-600 ring-4 ring-blue-200 flex items-center justify-center shadow-lg" data-id="element-1350">
              <MapPin size={14} className="text-white" data-id="element-1351" />
            </div>
            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-blue-600" data-id="element-1352" />
          </div>
        </motion.div>

        {/* Ripple */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none" data-id="element-1353">
          <span className="block w-8 h-8 rounded-full bg-blue-500/20 animate-ping" data-id="element-1354" />
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-stretch divide-x divide-gray-100 border-t border-gray-100 bg-white" data-id="element-1355">
        <button onClick={onRepin} disabled={isLocating} className="flex-1 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-50 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-60" data-id="element-1356">
          <Crosshair size={13} data-id="element-1357" />
          Re-pin
        </button>
        <button onClick={onClear} className="flex-1 py-2 text-xs font-semibold text-gray-500 hover:bg-gray-50 transition-colors" data-id="element-1358">
          Clear pin
        </button>
      </div>
    </div>;
}
function TelebirrLogo() {
  return <div className="w-9 h-9 rounded-md bg-white flex items-center justify-center" data-id="element-1359">
      <svg viewBox="0 0 32 32" className="w-7 h-7" aria-hidden="true" data-id="element-1360">
        <circle cx="16" cy="16" r="14" fill="#0066b3" data-id="element-1361" />
        <path d="M8 18 L16 10 L24 18 L20 18 L20 24 L12 24 L12 18 Z" fill="white" data-id="element-1362" />
        <text x="16" y="29" textAnchor="middle" fontSize="6" fontWeight="700" fill="#0066b3" data-id="element-1363">
          telebirr
        </text>
      </svg>
    </div>;
}